/**
 * Primary Fit Guide — fitguide.js
 * ================================
 * Logic + storage layer for the Primary Fit Guide widget.
 * Requires fitguide-tables.js to be loaded first (exposes FitGuideTables on window).
 *
 * Public API:
 *   FitGuide.recommend(inputs, fitValue) → result object
 *   FitGuide.storage.save(band, cupIdx)
 *   FitGuide.storage.load() → {primaryBand, primaryCupIndex, savedAt} | null
 *   FitGuide.storage.clear()
 *
 * inputs object shape:
 *   { method: 'C' | 'A' | 'B',
 *     // Option C — measurements
 *     underbust: Number,   // cm
 *     overbust:  Number,   // cm
 *     // Option A — bra size
 *     band:    Number,     // 6–16
 *     cup:     String,     // AA–G
 *     bandFit: 'tight' | 'well' | 'loose',   // optional, default 'well'
 *     cupFit:  'small' | 'well' | 'large',   // optional, default 'well'
 *     // Option B — t-shirt size
 *     tshirtSize: String,  // XS / S / M / L / XL
 *     bustShape:  String,  // 'flat' | 'small' | 'average' | 'full' | 'large'
 *   }
 */

/* eslint-disable */
(function (root) {
  'use strict';

  // ── Constants ──────────────────────────────────────────────────────────────

  var BANDS = [6, 8, 10, 12, 14, 16];

  // Primary sizing system: A–G only (AA / DD are sister-size display values only)
  var PRIMARY_CUPS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
  var PRIMARY_CUP_IDX = { A: 0, B: 1, C: 2, D: 3, E: 4, F: 5, G: 6 };

  // Full cup list including AA / DD — used for Option A input normalisation
  var FULL_CUP_IDX = { AA: 0, A: 1, B: 2, C: 3, D: 4, DD: 5, E: 6, F: 7, G: 8 };
  // Mapping from FULL_CUP_IDX to nearest PRIMARY_CUP index (0-based)
  // AA→A(0), A→A(0), B→B(1), C→C(2), D→D(3), DD→D(3), E→E(4), F→F(5), G→G(6)
  var FULL_TO_PRIMARY = [0, 0, 1, 2, 3, 3, 4, 5, 6];

  // ── Custom Rules ───────────────────────────────────────────────────────────
  // Post-lookup overrides based on real-world brand stocking decisions.
  // Applied in order; later rules see mutations from earlier rules.
  // result.rulesApplied → [{ruleId, description, field, from, to}]

  var TACT_SIZE_ORDER  = ['XS', 'S', 'M', 'L', 'XL'];
  var CUP_ORDER_FULL   = ['AA', 'A', 'B', 'C', 'D', 'DD', 'E', 'F', 'G'];
  var CUP_E_IDX        = CUP_ORDER_FULL.indexOf('E'); // 6

  // Specific sister size overrides; keyed by auKey (band+cup string)
  // Must run before SISTER-AA-TO-A / SISTER-FF-TO-F so AA/FF rules don't double-fire
  var SISTER_OVERRIDES = {
    // sisterLooser
    '10A':  { sisterLooser: '12B'  },
    '10B':  { sisterLooser: '12B'  },
    '10E':  { sisterLooser: '12E'  },
    '12A':  { sisterLooser: null   },
    '12B':  { sisterLooser: null   },
    '12E':  { sisterLooser: '14E'  },
    // sisterTighter
    '10D':  { sisterTighter: '8E'  },
    '10DD': { sisterTighter: '8E'  },
    '12D':  { sisterTighter: '10E' },
    '12DD': { sisterTighter: '10E' },
    '14D':  { sisterTighter: '12E' },
    '16D':  { sisterTighter: '14E' },
  };

  var CUSTOM_RULES = [
    {
      id: 'SISTER-OVERRIDES',
      description: 'Specific sister size corrections for more accurate fit recommendations',
      apply: function (result, band, cup) {
        var key = result.auKey;
        var ov = SISTER_OVERRIDES[key];
        if (!ov) return [];
        var changes = [];
        if ('sisterTighter' in ov && result.sisterTighter !== ov.sisterTighter) {
          changes.push({ field: 'sisterTighter', from: result.sisterTighter, to: ov.sisterTighter });
        }
        if ('sisterLooser' in ov && result.sisterLooser !== ov.sisterLooser) {
          changes.push({ field: 'sisterLooser', from: result.sisterLooser, to: ov.sisterLooser });
        }
        return changes;
      }
    },
    {
      id: 'SISTER-AA-TO-A',
      description: 'AA cup sister sizes replaced with A cup (AA not stocked as a standalone size)',
      apply: function (result) {
        var changes = [];
        if (result.sisterTighter && result.sisterTighter.slice(-2) === 'AA') {
          changes.push({ field: 'sisterTighter', from: result.sisterTighter, to: result.sisterTighter.slice(0, -2) + 'A' });
        }
        if (result.sisterLooser && result.sisterLooser.slice(-2) === 'AA') {
          changes.push({ field: 'sisterLooser', from: result.sisterLooser, to: result.sisterLooser.slice(0, -2) + 'A' });
        }
        return changes;
      }
    },
    {
      id: 'SISTER-FF-TO-F',
      description: 'FF cup sister sizes replaced with F cup (FF is not a standard AU cup size)',
      apply: function (result) {
        var changes = [];
        if (result.sisterTighter && result.sisterTighter.slice(-2) === 'FF') {
          changes.push({ field: 'sisterTighter', from: result.sisterTighter, to: result.sisterTighter.slice(0, -2) + 'F' });
        }
        if (result.sisterLooser && result.sisterLooser.slice(-2) === 'FF') {
          changes.push({ field: 'sisterLooser', from: result.sisterLooser, to: result.sisterLooser.slice(0, -2) + 'F' });
        }
        return changes;
      }
    },
    {
      id: 'TACT-XL',
      description: 'Tact does not stock sizes above XL',
      apply: function (result) {
        var v = result.tact;
        if (v && TACT_SIZE_ORDER.indexOf(v) === -1) {
          return [{ field: 'tact', from: v, to: null }];
        }
        return [];
      }
    },
    {
      id: 'ELSE-DD-LETTER',
      description: 'DD cup Else letter size matches E cup (fallback: D cup) for same band',
      apply: function (result, band, cup, T) {
        if (cup !== 'DD') return [];
        var eEntry = T.masterLookup[band + 'E'];
        var dEntry = T.masterLookup[band + 'D'];
        var letter = (eEntry && eEntry.elseLetter) || (dEntry && dEntry.elseLetter) || null;
        if (letter !== result.elseLetter) {
          return [{ field: 'elseLetter', from: result.elseLetter, to: letter }];
        }
        return [];
      }
    },
    {
      id: 'ELSE-LETTER-CORRECTIONS',
      description: "Corrects Else letter sizes to match Else's current size labelling (6–16 band updates)",
      apply: function (result, band, cup) {
        var CORRECTIONS = {
          '6A':'XS','6B':'XS','6C':'XS','6D':'XS',
          '6DD':'S','6E':'S','6F':'S',
          '6G':'M',
          '8DD':'M','8E':'M',
          '8F':'L','8G':'L',
          '10DD':'XL','10D':'M','10E':'XL','10F':'XL',
          '12DD':'XL','12D':'L','12E':'XL','12F':'XL',
          '14DD':'XL','14A':'L','14E':'XL',
          '16DD':'XL','16A':'L','16B':'L','16C':'XL','16D':'XL',
        };
        var key = band + cup;
        if (!CORRECTIONS.hasOwnProperty(key)) return [];
        var target = CORRECTIONS[key];
        if (target === result.elseLetter) return [];
        return [{ field: 'elseLetter', from: result.elseLetter, to: target }];
      }
    },
    {
      id: 'LS-CORRECTIONS',
      description: 'Love & Swans size corrections per brand size chart',
      apply: function (result, band, cup) {
        var LS_MAP = {
          '8A':'10AB','8B':'10AB','8C':'10AB',
          '8D':'10CD','8DD':'10CD',
          '8E':'10EF','8F':'10EF','8G':'10EF',
          '10DD':'10EF','10E':'10EF',
          '10F':'12EF',
          '12B':'12AB',
          '12E':'12EF','12F':'12EF','12G':'12EF',
          '14B':'14AB',
          '14E':'14EF','14F':'14EF','14G':'14EF',
          '16E':'16EF','16F':'16EF',
        };
        var target = LS_MAP[band + cup];
        if (target === undefined) return [];
        if (target === result.lsSoft) return [];
        return [{ field: 'lsSoft', from: result.lsSoft, to: target }];
      }
    },
    {
      id: 'TACT-LETTER',
      description: "TA-CT letter sizing matches Else letter sizing",
      apply: function (result) {
        var target = result.elseLetter;
        if (target === result.tact) return [];
        return [{ field: 'tact', from: result.tact, to: target }];
      }
    },
    {
      id: 'NR-E-LIMIT',
      description: 'Nette Rose does not stock bras above E cup',
      apply: function (result, band, cup) {
        if (CUP_ORDER_FULL.indexOf(cup) > CUP_E_IDX) {
          if (result.nrBra !== null) return [{ field: 'nrBra', from: result.nrBra, to: null }];
        }
        return [];
      }
    },
    {
      id: 'NR-BRALETTE',
      description: 'NR bralette matches Else letter size across all cup sizes',
      apply: function (result) {
        var target = result.elseLetter || null;
        if (target !== result.nrBralette) {
          return [{ field: 'nrBralette', from: result.nrBralette, to: target }];
        }
        return [];
      }
    },
  ];

  function applyCustomRules(result, band, cup, T) {
    var rulesApplied = [];
    for (var i = 0; i < CUSTOM_RULES.length; i++) {
      var rule = CUSTOM_RULES[i];
      var changes = rule.apply(result, band, cup, T);
      for (var j = 0; j < changes.length; j++) {
        var ch = changes[j];
        result[ch.field] = ch.to;
        rulesApplied.push({ ruleId: rule.id, description: rule.description, field: ch.field, from: ch.from, to: ch.to });
      }
    }
    return rulesApplied;
  }

  // Option B: t-shirt size → AU band
  // Option B: t-shirt size + bust shape → explicit AU key
  // [smaller, average, fuller] — DD entries fall back to D if not in masterLookup
  var TSHIRT_MAP = {
    XS: ['8B',  '8C',  '8D' ],
    S:  ['8C',  '8D',  '8E' ],
    M:  ['10B', '10D', '12DD'],
    L:  ['12C', '12D', '14E' ],
    XL: ['14C', '14D', '14E' ],
  };
  var BUST_TO_IDX = { flat: 0, small: 0, average: 1, full: 2, large: 2 };

  // Confidence scores by method
  var CONFIDENCE = { C: 5, A: 3, B: 1 };
  var CONFIDENCE_LABEL = {
    5: 'High confidence — based on your measurements',
    3: 'Good fit — refined from your bra size',
    1: 'Estimated — add measurements for higher accuracy',
  };

  // ── Table reference ────────────────────────────────────────────────────────

  function tables() {
    if (!root.FitGuideTables) {
      throw new Error('FitGuide: fitguide-tables.js must be loaded before fitguide.js');
    }
    return root.FitGuideTables;
  }

  // ── Step-table lookup ──────────────────────────────────────────────────────
  // Returns the last entry where entry[key] <= value.

  function stepLookup(steps, key, value) {
    var result = null;
    for (var i = 0; i < steps.length; i++) {
      if (steps[i][key] <= value) result = steps[i];
      else break;
    }
    return result;
  }

  // ── Band from underbust ────────────────────────────────────────────────────

  function getBandFromUnderbust(ub) {
    var T = tables();
    var step = stepLookup(T.primaryBandSteps, 'ubGte', ub);
    return step ? step.band : null;
  }

  // ── Cup index from overbust — Option C only ────────────────────────────────
  // Returns index into PRIMARY_CUPS (0=A … 6=G).
  // Finds last cup whose lower bound for this band is ≤ overbust.

  function getCupIdxFromOverbust(ob, band) {
    var T = tables();
    var bandStr = String(band);
    var result = 0; // floor at A
    for (var i = 0; i < PRIMARY_CUPS.length; i++) {
      var cup = PRIMARY_CUPS[i];
      var bounds = T.primaryCupLowerBounds[cup];
      if (bounds && bounds[bandStr] !== undefined && ob >= bounds[bandStr]) {
        result = i;
      }
    }
    return result;
  }

  // ── Nearest valid band ─────────────────────────────────────────────────────

  function clampBand(band) {
    if (BANDS.indexOf(band) !== -1) return band;
    var nearest = BANDS[0];
    for (var i = 1; i < BANDS.length; i++) {
      if (Math.abs(BANDS[i] - band) < Math.abs(nearest - band)) nearest = BANDS[i];
    }
    return nearest;
  }

  // ── Input resolution ───────────────────────────────────────────────────────
  // Returns: { band, cupIdx, underbust, overbust, method, confidence }

  function resolveInputs(inputs) {
    var T = tables();
    var band, cupIdx, ub, ob;

    if (inputs.method === 'C') {
      ub = parseFloat(inputs.underbust);
      ob = parseFloat(inputs.overbust);
      if (isNaN(ub) || isNaN(ob)) return null;
      band = getBandFromUnderbust(ub);
      if (!band) return null;
      cupIdx = getCupIdxFromOverbust(ob, band);
      return { band: band, cupIdx: cupIdx, underbust: ub, overbust: ob, method: 'C', confidence: 5 };
    }

    if (inputs.method === 'A') {
      band = parseInt(inputs.band, 10);
      if (isNaN(band)) return null;

      // Normalise cup from full list (AA/DD) to primary index
      var cupStr = String(inputs.cup || '').toUpperCase().trim();
      var fullIdx = FULL_CUP_IDX[cupStr];
      cupIdx = (fullIdx !== undefined) ? FULL_TO_PRIMARY[fullIdx] : 0;

      // Band fit adjustment (applied to stated band first)
      var bandFit = inputs.bandFit || 'well';
      if (bandFit === 'tight') band = band + 2;
      else if (bandFit === 'loose') band = band - 2;
      band = clampBand(band);

      // Cup fit adjustment
      var cupFit = inputs.cupFit || 'well';
      if (cupFit === 'small') cupIdx = Math.min(cupIdx + 1, PRIMARY_CUPS.length - 1);
      else if (cupFit === 'large') cupIdx = Math.max(cupIdx - 1, 0);

      // Estimate underbust/overbust from midpoints (for brand calcs needing raw cm)
      var auKeyA = band + PRIMARY_CUPS[cupIdx];
      var entryA = T.masterLookup[auKeyA];
      ub = entryA ? entryA.ubMid : null;
      ob = entryA ? entryA.obMid : null;
      return { band: band, cupIdx: cupIdx, underbust: ub, overbust: ob, method: 'A', confidence: 3 };
    }

    if (inputs.method === 'B') {
      var tshirt = String(inputs.tshirtSize || '').toUpperCase().trim();
      var tshirtKeys = TSHIRT_MAP[tshirt];
      if (!tshirtKeys) return null;

      var shape = String(inputs.bustShape || '').toLowerCase().trim();
      var bustIdx = BUST_TO_IDX[shape];
      if (bustIdx === undefined) bustIdx = 1; // default average

      var auKeyB = tshirtKeys[bustIdx];
      var entryB = T.masterLookup[auKeyB];
      if (!entryB) return null;

      ub = entryB.ubMid;
      ob = entryB.obMid;
      var cupB = String(entryB.cup);
      var cupIdxB = PRIMARY_CUPS.indexOf(cupB);
      if (cupIdxB === -1) cupIdxB = 3; // fallback to D
      return { band: entryB.band, cupIdx: cupIdxB, underbust: ub, overbust: ob, method: 'B', confidence: 1 };
    }

    return null;
  }

  // ── fit_value cup adjustment ───────────────────────────────────────────────
  // fitValue: 0–100 integer (or null/undefined → treated as 50)
  // 0–33: cups small → cup up one; 34–66: no change; 67–100: cups large → cup down one

  function applyFitValue(cupIdx, fitValue) {
    var fv = (fitValue === null || fitValue === undefined) ? 50 : parseInt(fitValue, 10);
    if (isNaN(fv)) fv = 50;
    if (fv <= 33) return Math.min(cupIdx + 1, PRIMARY_CUPS.length - 1);
    if (fv >= 67) return Math.max(cupIdx - 1, 0);
    return cupIdx;
  }

  // ── Full recommendation ────────────────────────────────────────────────────
  // inputs: see module docblock
  // fitValue: product metafield custom.fit_value (0–100, or null)
  // Returns: result object, or null if inputs are invalid

  function recommend(inputs, fitValue) {
    var resolved = resolveInputs(inputs);
    if (!resolved || resolved.band === null) return null;

    var T = tables();
    var adjCupIdx = applyFitValue(resolved.cupIdx, fitValue);
    var cup = PRIMARY_CUPS[adjCupIdx];
    var auKey = resolved.band + cup;
    var entry = T.masterLookup[auKey];

    if (!entry) return null;

    var result = {
      // Primary / Else
      primary:      auKey,        // e.g. "12C" (kept for backwards compat)
      elseBra:      entry.elseBra,
      elseLetter:   entry.elseLetter,

      // Other brands — pre-computed per adjusted AU key
      // null = not available / out of range for that brand
      nrBra:        entry.nrBra,
      nrBralette:   entry.nrBralette,
      usStandard:   entry.usStandard,
      usSupport:    entry.usSupport,
      maisonClose:  entry.maisonClose,
      tact:         entry.tact,

      lsSoft:       entry.lsSoft,

      // Sister sizes — pre-computed in masterLookup; rules may override below
      sisterTighter: entry.sisterTighter || null,
      sisterLooser:  entry.sisterLooser  || null,

      // Measurements (midpoints in cm)
      ubMid:           entry.ubMid,
      obMid:           entry.obMid,

      // Meta
      auKey:           auKey,
      band:            resolved.band,
      cup:             cup,
      method:          resolved.method,
      confidence:      resolved.confidence,
      confidenceLabel: CONFIDENCE_LABEL[resolved.confidence] || '',
    };

    result.rulesApplied = applyCustomRules(result, resolved.band, cup, T);
    result.sisters = { tighter: result.sisterTighter, looser: result.sisterLooser };
    return result;
  }

  // ── Understatement paired-product helper ───────────────────────────────────
  // Returns display object given product context metafields.
  // isSupport: custom.fit_us_support_bra (true = Mesh Support Bra)
  // equivProductUrl: custom.fit_equiv_product resolved URL (or null)

  function resolveUnderstatement(result, isSupport, equivProductUrl) {
    if (!result) return null;
    var primary = isSupport ? result.usSupport : result.usStandard;
    var alt     = isSupport ? result.usStandard : result.usSupport;
    var altLabel = isSupport ? 'the Triangle Bralette' : 'Mesh Support Bra';
    var copy;

    if (primary && alt && equivProductUrl) {
      copy = isSupport
        ? 'For a more flexible fit, you can choose ' + altLabel + ' in <strong>' + alt + '</strong>.'
        : 'For extra structure, you can choose ' + altLabel + ' in <strong>' + alt + '</strong>.';
    }

    return {
      primary:        primary,
      alt:            alt,
      altLabel:       altLabel,
      altUrl:         equivProductUrl || null,
      altCopy:        copy || null,
    };
  }

  // ── Storage ────────────────────────────────────────────────────────────────
  var STORAGE_KEY = 'primary_fit_guide';

  var Storage = {
    save: function (band, cupIdx) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          primaryBand:     band,
          primaryCupIndex: cupIdx,
          savedAt:         Date.now(),
        }));
      } catch (e) { /* storage unavailable */ }
      try {
        document.dispatchEvent(new CustomEvent('fitguide:saved', {
          detail: { band: band, cupIdx: cupIdx },
          bubbles: true,
        }));
      } catch (e) {}
    },

    load: function () {
      try {
        var raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) { return null; }
    },

    clear: function () {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    },

    // Convenience: saved primary size string e.g. "12C", or null
    savedSizeLabel: function () {
      var d = Storage.load();
      if (!d || d.primaryBand === undefined || d.primaryCupIndex === undefined) return null;
      return d.primaryBand + PRIMARY_CUPS[d.primaryCupIndex];
    },
  };

  // ── Klaviyo helpers ────────────────────────────────────────────────────────
  var Klaviyo = {
    _fire: function (fn) {
      if (typeof klaviyo !== 'undefined') {
        fn(klaviyo);
      } else if (typeof window !== 'undefined') {
        // Mirror wishlist.js pattern: 2s fallback
        var t = 0;
        var poll = setInterval(function () {
          t += 100;
          if (typeof klaviyo !== 'undefined') { clearInterval(poll); fn(klaviyo); }
          else if (t >= 2000) clearInterval(poll);
        }, 100);
      }
    },

    identify: function (band, cupLabel, method, inputs) {
      var METHOD_LABELS = { A: 'bra-size', B: 't-shirt', C: 'measurements' };
      var methodLabel = METHOD_LABELS[method] || method;
      var cleanInputs = null;
      if (inputs) {
        cleanInputs = {};
        for (var k in inputs) {
          if (inputs.hasOwnProperty(k) && k !== 'method') cleanInputs[k] = inputs[k];
        }
      }
      Klaviyo._fire(function (kl) {
        kl.identify({
          fit_primary_size:   band + cupLabel,
          fit_method:         methodLabel,
          fit_inputs:         cleanInputs,
          fit_saved_at:       new Date().toISOString(),
        });
      });
    },

    track: function (method, result, pageUrl) {
      var METHOD_LABELS = { A: 'bra-size', B: 't-shirt', C: 'measurements' };
      Klaviyo._fire(function (kl) {
        kl.track('Fit Guide - Completed', {
          method:       METHOD_LABELS[method] || method,
          primary_size: result ? result.primary : null,
          inputs:       result ? result.inputs  : null,
          page_url:     pageUrl || null,
        });
      });
    },

    // Subscribe email to marketing list via Klaviyo client subscriptions API.
    // $consent on identify() is not honoured by the new klaviyo.js — requires
    // an explicit POST to /client/subscriptions/.
    _subscribeEmail: function (email) {
      var listId = (root.FG_KLAVIYO_LIST_ID) || 'UBvyP4';
      var companyId = null;
      try {
        var scripts = document.querySelectorAll('script[src*="klaviyo.com"]');
        for (var i = 0; i < scripts.length; i++) {
          var m = scripts[i].src.match(/[?&]company_id=([A-Za-z0-9]+)/);
          if (m) { companyId = m[1]; break; }
        }
      } catch (e) {}
      if (!companyId) return;
      try {
        fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + companyId, {
          method: 'POST',
          headers: { 'content-type': 'application/json', 'revision': '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email: email },
                  },
                },
              },
              relationships: {
                list: { data: { type: 'list', id: listId } },
              },
            },
          }),
        }).catch(function () {});
      } catch (e) {}
    },
  };

  // ── Public API ─────────────────────────────────────────────────────────────

  root.FitGuide = {
    recommend:              recommend,
    resolveUnderstatement:  resolveUnderstatement,
    storage:                Storage,
    klaviyo:                Klaviyo,

    // Exposed for unit tests
    _getBandFromUnderbust:  getBandFromUnderbust,
    _getCupIdxFromOverbust: getCupIdxFromOverbust,
    _resolveInputs:         resolveInputs,
    _applyFitValue:         applyFitValue,
    _PRIMARY_CUPS:          PRIMARY_CUPS,
  };

}(typeof globalThis !== 'undefined' ? globalThis : this));
